import { useParams, Link } from 'react-router-dom';
import { useVerify } from '../../hooks/useVerify.js';
import { VerifyPreviewCard } from '../../components/verify/VerifyPreviewCard.jsx';

/**
 * Public verify page — what members of the public see after scanning a
 * QR code. Route: /v/:tenantSlug/:staffSlug
 *
 * UNAUTHENTICATED. No session, no login, no signup nag. The whole point
 * is frictionless trust — a customer at their front door shouldn't need
 * an account to verify the person standing in front of them.
 *
 * Pulls directly from the public /api/verify endpoint. That endpoint
 * also logs a scan as a side effect, so visiting this page counts.
 *
 * Four result states, all handled by VerifyPreviewCard:
 *   verified / under_review / no_longer_employed / unknown
 *
 * Unknown covers two sub-cases: bad tenant slug, bad staff slug. Both
 * show the same "this code doesn't match" message — we don't distinguish
 * because either way the customer's trust signal is "don't let them in".
 *
 * Powered-by-TocynID footer lives here — not on the admin dashboard.
 * Platform visibility for potential customers scanning a vetted
 * worker's card.
 */
export function VerifyPublicPage() {
  const { tenantSlug, staffSlug } = useParams();
  const { data, loading, error } = useVerify(tenantSlug, staffSlug);

  return (
    <div className="verify-page">
      {loading && (
        <div role="status" style={{ textAlign: 'center' }}>
          <div className="spinner" aria-hidden="true" />
          <p className="muted" style={{ marginTop: 'var(--sp-3)' }}>Verifying…</p>
        </div>
      )}

      {error && !loading && (
        <div className="verify-card">
          <div className="verify-header">
            <p className="verify-tenant-name">Connection error</p>
          </div>
          <div className="verify-body">
            <p>
              We couldn't reach the verification service. Check your internet
              connection and try again, or contact the organisation directly.
            </p>
          </div>
        </div>
      )}

      {!loading && !error && data && (
        <VerifyPreviewCard data={data} />
      )}

      <div className="verify-powered">
        Powered by{' '}
        <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
          <span className="brand-tocyn">Tocyn</span>
          <span className="brand-id">ID</span>
        </Link>
      </div>
    </div>
  );
}
